const { VITE_ANALYSIS_AGENT_API } = import.meta.env;

// Agents analysis-agent mounts a run-status router for (`build_run_status_router`
// in `routers/v1/airflow_run_status.py`); the path is `<agent>-airflow-runs`.
export const PUBLIC_DATASET_AGENT_NAME = "public-dataset-agent";
export const VCF_INGEST_AGENT_NAME = "vcf-ingest-agent";
export type RunStatusAgent = typeof PUBLIC_DATASET_AGENT_NAME | typeof VCF_INGEST_AGENT_NAME;

export const runStatusPath = (agent: RunStatusAgent, dagId: string, runId: string) =>
  `${VITE_ANALYSIS_AGENT_API}/${agent}-airflow-runs/${encodeURIComponent(dagId)}/${encodeURIComponent(runId)}/status`;

// A TaskInstance's `state` vocabulary — larger than a DagRun's own `state`.
// Mirrors `agent_utils.clients.airflow_client.TaskInstanceState`. `null` (no
// state yet) is distinct from the literal string `"none"` (a deprecated but
// real Airflow state).
export type TaskInstanceState =
  | "none"
  | "scheduled"
  | "queued"
  | "running"
  | "success"
  | "restarting"
  | "failed"
  | "up_for_retry"
  | "up_for_reschedule"
  | "upstream_failed"
  | "skipped"
  | "removed"
  | "deferred";

// One task instance within a run. `mapIndex` is `-1` for a normal task, or
// `>= 0` for one instance of a dynamically-mapped task (Airflow's `.expand()`)
// — several instances then share one `taskId`.
export interface TaskInstanceStatus {
  taskId: string;
  mapIndex: number;
  state: TaskInstanceState | null;
  startDate: string | null;
  endDate: string | null;
  duration: number | null;
  tryNumber: number;
}

export const AirflowRunState = {
  QUEUED: "queued",
  RUNNING: "running",
  SUCCESS: "success",
  FAILED: "failed",
} as const;
export type AirflowRunState = (typeof AirflowRunState)[keyof typeof AirflowRunState];

// succeeded/failed are terminal and never change again; queued/running are
// still in flight. A run that reaches a terminal state stops polling.
export const AIRFLOW_RUN_TERMINAL_STATES: ReadonlySet<string> = new Set([
  AirflowRunState.SUCCESS,
  AirflowRunState.FAILED,
]);

export interface DagRunStatus {
  dagId: string;
  runId: string;
  state: AirflowRunState;
  executionDate: string | null;
  startDate: string | null;
  endDate: string | null;
  tasks: TaskInstanceStatus[];
}
